import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  BookmarkSimple,
  Bell,
  User,
  SignOut,
  Trash,
  Plus,
  Clock,
  CaretUp,
  CaretDown,
  GearSix,
  Crown,
  Check,
  Envelope,
  CalendarCheck,
  ToggleLeft,
  ToggleRight
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function UserDashboard() {
  const { user, token, logout, deletePriceAlert, createPriceAlert, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("saved");
  const [savedArticles, setSavedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [newAlert, setNewAlert] = useState({ commodity: "WHEAT", condition: "above", price: 0 });
  const [commodities, setCommodities] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [preferences, setPreferences] = useState({ weekly_report: false, email_alerts: true });
  const [prefsLoading, setPrefsLoading] = useState(false);

  // Check for payment session
  const sessionId = searchParams.get("session_id");

  const checkPaymentStatus = useCallback(async (sid) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Payment verification timed out.");
        setCheckingPayment(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === "paid") {
          toast.success("Subscription activated! You now have premium features.");
          setCheckingPayment(false);
          window.location.reload();
          return;
        }

        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [articlesRes, commoditiesRes, prefsRes] = await Promise.all([
        axios.get(`${API}/user/saved-articles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/commodities`),
        axios.get(`${API}/user/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSavedArticles(articlesRes.data);
      setCommodities(commoditiesRes.data);
      setPreferences(prefsRes.data);
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (sessionId && user && token) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, user, token, checkPaymentStatus]);

  useEffect(() => {
    if (user && !checkingPayment) {
      fetchData();
    } else if (!authLoading && !checkingPayment) {
      setLoading(false);
    }
  }, [user, authLoading, checkingPayment, fetchData]);

  const toggleWeeklyReport = async () => {
    setPrefsLoading(true);
    try {
      const response = await axios.put(`${API}/user/weekly-report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(prev => ({ ...prev, weekly_report: response.data.weekly_report }));
      toast.success(response.data.weekly_report 
        ? "Weekly report enabled - you'll receive market summaries every Monday" 
        : "Weekly report disabled"
      );
    } catch (error) {
      toast.error("Failed to update preference");
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    try {
      await createPriceAlert(newAlert);
      toast.success("Price alert created");
      setShowAlertForm(false);
      setNewAlert({ commodity: "WHEAT", condition: "above", price: 0 });
    } catch (error) {
      toast.error("Failed to create alert");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await deletePriceAlert(alertId);
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[900px] mx-auto px-4 py-12 text-center">
          <div className="animate-pulse text-[#6B6661]">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[900px] mx-auto px-4 py-12 text-center">
          <h1 className="font-serif text-3xl text-[#33302E] mb-4">Sign In Required</h1>
          <p className="text-[#6B6661]">Please sign in to access your dashboard.</p>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const regionLabels = {
    ireland: "Ireland",
    uk: "UK",
    usa: "USA",
    china: "China",
    india: "India",
    brazil: "Brazil",
    global: "Global"
  };

  const subscriptionBadge = {
    free: { label: "Free", color: "bg-[#E5E7EB] text-[#6B6661]" },
    premium: { label: "Premium", color: "bg-[#0F766E] text-white" },
    pro: { label: "Professional", color: "bg-[#D97706] text-white" }
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />
      
      {checkingPayment && (
        <div className="bg-[#0F766E] text-white py-3 text-center">
          <span className="animate-pulse">Verifying your payment...</span>
        </div>
      )}
      
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 pb-6 border-b border-[#E0D4C8] gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#0F766E] flex items-center justify-center">
              <User size={32} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-serif text-2xl text-[#33302E]">{user.name}</h1>
                <span className={`px-2 py-0.5 text-xs font-mono uppercase tracking-wider ${subscriptionBadge[user.subscription || "free"].color}`}>
                  {subscriptionBadge[user.subscription || "free"].label}
                </span>
              </div>
              <p className="text-sm text-[#6B6661] font-sans">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(user.subscription === "free" || !user.subscription) && (
              <Link
                to="/pricing"
                data-testid="upgrade-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D9488] transition-colors"
              >
                <Crown size={18} />
                Upgrade
              </Link>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:border-[#DC2626] hover:text-[#DC2626] transition-colors"
            >
              <SignOut size={18} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab("saved")}
            data-testid="tab-saved"
            className={`flex items-center gap-2 px-4 py-2 font-sans text-sm border-b-2 transition-colors ${
              activeTab === "saved"
                ? "border-[#0F766E] text-[#0F766E]"
                : "border-transparent text-[#6B6661] hover:text-[#33302E]"
            }`}
          >
            <BookmarkSimple size={18} />
            Saved Articles ({savedArticles.length})
          </button>
          <button
            onClick={() => setActiveTab("alerts")}
            data-testid="tab-alerts"
            className={`flex items-center gap-2 px-4 py-2 font-sans text-sm border-b-2 transition-colors ${
              activeTab === "alerts"
                ? "border-[#0F766E] text-[#0F766E]"
                : "border-transparent text-[#6B6661] hover:text-[#33302E]"
            }`}
          >
            <Bell size={18} />
            Price Alerts ({user.price_alerts?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            data-testid="tab-settings"
            className={`flex items-center gap-2 px-4 py-2 font-sans text-sm border-b-2 transition-colors ${
              activeTab === "settings"
                ? "border-[#0F766E] text-[#0F766E]"
                : "border-transparent text-[#6B6661] hover:text-[#33302E]"
            }`}
          >
            <GearSix size={18} />
            Settings
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-[#6B6661]">Loading...</div>
          </div>
        ) : (
          <>
            {/* Saved Articles */}
            {activeTab === "saved" && (
              <div>
                {savedArticles.length === 0 ? (
                  <div className="text-center py-12 border border-[#E0D4C8] bg-white">
                    <BookmarkSimple size={48} className="mx-auto mb-4 text-[#E5E7EB]" />
                    <h3 className="font-serif text-xl text-[#33302E] mb-2">No saved articles</h3>
                    <p className="text-[#6B6661] mb-4">Save articles to read them later</p>
                    <Link
                      to="/"
                      className="inline-block px-6 py-2 bg-[#111827] text-white font-sans text-sm hover:bg-[#0F766E] transition-colors"
                    >
                      Browse News
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {savedArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="bg-white border border-[#E0D4C8] hover:border-[#0F766E] transition-colors"
                      >
                        {article.image_url && (
                          <img
                            src={article.image_url}
                            alt={article.title}
                            className="w-full h-40 object-cover"
                          />
                        )}
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-[#E5E7EB] text-[#6B6661] text-[10px] font-mono uppercase">
                              {regionLabels[article.region]}
                            </span>
                            <span className="text-xs text-[#6B6661] font-mono">
                              {formatDate(article.published_at)}
                            </span>
                          </div>
                          <h3 className="font-serif text-lg text-[#33302E] line-clamp-2">
                            {article.title}
                          </h3>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Price Alerts */}
            {activeTab === "alerts" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl text-[#33302E]">Your Price Alerts</h2>
                  <button
                    onClick={() => setShowAlertForm(!showAlertForm)}
                    data-testid="create-alert-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-[#111827] text-white font-sans text-sm hover:bg-[#0F766E] transition-colors"
                  >
                    <Plus size={16} />
                    New Alert
                  </button>
                </div>

                {showAlertForm && (
                  <form onSubmit={handleCreateAlert} className="bg-white border border-[#E0D4C8] p-6 mb-6">
                    <h3 className="font-serif text-lg text-[#33302E] mb-4">Create Price Alert</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
                          Commodity
                        </label>
                        <select
                          value={newAlert.commodity}
                          onChange={(e) => setNewAlert({ ...newAlert, commodity: e.target.value })}
                          className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
                        >
                          {commodities.map((c) => (
                            <option key={c.symbol} value={c.symbol}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
                          Condition
                        </label>
                        <select
                          value={newAlert.condition}
                          onChange={(e) => setNewAlert({ ...newAlert, condition: e.target.value })}
                          className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
                        >
                          <option value="above">Price goes above</option>
                          <option value="below">Price goes below</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
                          Price ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={newAlert.price}
                          onChange={(e) => setNewAlert({ ...newAlert, price: parseFloat(e.target.value) })}
                          required
                          className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <button
                        type="submit"
                        className="px-6 py-2 bg-[#0F766E] text-white font-sans text-sm hover:bg-[#0D9488] transition-colors"
                      >
                        Create Alert
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAlertForm(false)}
                        className="px-6 py-2 border border-[#E0D4C8] text-[#6B6661] font-sans text-sm hover:border-[#111827] transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {(!user.price_alerts || user.price_alerts.length === 0) ? (
                  <div className="text-center py-12 border border-[#E0D4C8] bg-white">
                    <Bell size={48} className="mx-auto mb-4 text-[#E5E7EB]" />
                    <h3 className="font-serif text-xl text-[#33302E] mb-2">No price alerts</h3>
                    <p className="text-[#6B6661]">Create alerts to get notified when prices change</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {user.price_alerts.map((alert) => {
                      const commodity = commodities.find(c => c.symbol === alert.commodity);
                      return (
                        <div
                          key={alert.id}
                          className="flex items-center justify-between bg-white border border-[#E0D4C8] p-4"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 flex items-center justify-center ${
                              alert.condition === "above" ? "bg-[#F0FDF4]" : "bg-[#FEF2F2]"
                            }`}>
                              {alert.condition === "above" ? (
                                <CaretUp size={20} className="text-[#059669]" weight="bold" />
                              ) : (
                                <CaretDown size={20} className="text-[#DC2626]" weight="bold" />
                              )}
                            </div>
                            <div>
                              <p className="font-sans font-medium text-[#33302E]">
                                {commodity?.name || alert.commodity}
                              </p>
                              <p className="text-sm text-[#6B6661]">
                                Alert when price goes {alert.condition} ${alert.price.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {commodity && (
                              <div className="text-right">
                                <p className="font-mono text-lg text-[#33302E]">${commodity.price.toFixed(2)}</p>
                                <p className="text-xs text-[#6B6661]">Current</p>
                              </div>
                            )}
                            <button
                              onClick={() => handleDeleteAlert(alert.id)}
                              className="p-2 text-[#6B6661] hover:text-[#DC2626] transition-colors"
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <div>
                <h2 className="font-serif text-xl text-[#33302E] mb-6">Notification Preferences</h2>
                
                <div className="space-y-4">
                  {/* Weekly Market Report */}
                  <div className="bg-white border border-[#E0D4C8] p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#F0FDF4] flex items-center justify-center">
                          <CalendarCheck size={24} className="text-[#0F766E]" />
                        </div>
                        <div>
                          <h3 className="font-sans font-medium text-[#33302E] mb-1">Weekly Market Report</h3>
                          <p className="text-sm text-[#6B6661] max-w-md">
                            Receive a summary email every Monday with your watchlist prices, county league changes, and top-performing marts.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={toggleWeeklyReport}
                        disabled={prefsLoading}
                        data-testid="toggle-weekly-report"
                        className="mt-1"
                      >
                        {preferences.weekly_report ? (
                          <ToggleRight size={40} weight="fill" className="text-[#0F766E]" />
                        ) : (
                          <ToggleLeft size={40} className="text-[#8B8580]" />
                        )}
                      </button>
                    </div>
                    {preferences.weekly_report && (
                      <div className="mt-4 ml-16 px-3 py-2 bg-[#F0FDF4] text-[#059669] text-sm flex items-center gap-2">
                        <Check size={16} />
                        You'll receive your first report next Monday
                      </div>
                    )}
                  </div>

                  {/* Email Alerts */}
                  <div className="bg-white border border-[#E0D4C8] p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#FEF3C7] flex items-center justify-center">
                          <Envelope size={24} className="text-[#D97706]" />
                        </div>
                        <div>
                          <h3 className="font-sans font-medium text-[#33302E] mb-1">Email Price Alerts</h3>
                          <p className="text-sm text-[#6B6661] max-w-md">
                            Receive email notifications when your price alerts are triggered.
                            {user.subscription === "free" && (
                              <span className="block mt-1 text-[#D97706]">Upgrade to Premium for this feature.</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-1">
                        {user.subscription !== "free" ? (
                          <ToggleRight size={40} weight="fill" className="text-[#0F766E]" />
                        ) : (
                          <Link to="/pricing" className="text-xs text-[#0F766E] hover:underline">Upgrade</Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Subscription Info */}
                  <div className="bg-[#111827] text-white p-6 mt-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#8B8580] uppercase tracking-wider mb-1">Your Plan</p>
                        <p className="font-serif text-2xl">{user.subscription === "pro" ? "Professional" : user.subscription === "premium" ? "Premium" : "Free"}</p>
                      </div>
                      {user.subscription === "free" && (
                        <Link to="/pricing" className="px-6 py-2 bg-[#0F766E] text-white hover:bg-[#14B8A6] flex items-center gap-2">
                          <Crown size={18} />
                          Upgrade
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
